import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Lottie from "lottie-react";
import privacyPolicyAnimation from "../../assets/wired-flat-966-privacy-policy-hover-swipe.json";
import zoomMeetingDemo from "../../assets/Features_Bento_Grid/Zoom_meeting_Demo.png";
import { fadeUp, stagger, viewportOnce } from "../../lib/animations";

import ozowLogo from "../../assets/Features_Bento_Grid/logos_new/Ozow.svg";
import payfastLogo from "../../assets/Features_Bento_Grid/logos_new/Payfast.svg";
import paystackLogo from "../../assets/Features_Bento_Grid/logos_new/paystack.svg";
import peachPaymentsLogo from "../../assets/Features_Bento_Grid/logos_new/peach_payements.svg";
import stitchMoneyLogo from "../../assets/Features_Bento_Grid/logos_new/Stitic money.svg";
import yocoLogo from "../../assets/Features_Bento_Grid/logos_new/Yoco.svg";

/* ====== Shared style constants ====== */
const GOLD = "#C9A84C";
const CREAM = "#F0EBE1";
const CREAM_LO = "rgba(240, 235, 225, 0.45)";
const GOLD_BRIGHT = "#E8C878";
const GOLD_DIM = "rgba(184, 146, 42, 0.08)";
const PURPLE_GLOW = "rgba(139, 92, 214, 0.32)";
const GLASS_BG = "rgba(14, 12, 19, 0.72)";

const cardStyle = {
  background: "linear-gradient(135deg, rgba(14, 12, 19, 0.95), rgba(26, 22, 35, 0.92))",
  backdropFilter: "blur(20px) saturate(1.2)",
  borderRadius: 24,
  border: "1px solid transparent",
  backgroundClip: "padding-box",
  position: "relative",
  overflow: "hidden",
  transition: "all 500ms cubic-bezier(0.22,1,0.36,1)",
  transformStyle: "preserve-3d",
  perspective: "1000px",
};

const innerCardStyle = {
  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: 14,
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.1)",
};

const Pill = ({ children }) => (
  <span
    className="font-syne inline-flex items-center"
    style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: GOLD_BRIGHT,
      background: "linear-gradient(135deg, rgba(184, 146, 42, 0.15), rgba(184, 146, 42, 0.08))",
      border: "1px solid rgba(201, 168, 76, 0.25)",
      borderRadius: 20,
      padding: "4px 14px",
      boxShadow: "0 2px 8px rgba(184, 146, 42, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(8px)",
    }}
  >
    {children}
  </span>
);

/* Premium layered overlays with depth and light effects */
const CardOverlays = () => (
  <>
    {/* Animated top-edge gradient */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent, rgba(232, 200, 120, 0.6), rgba(201, 168, 76, 0.8), rgba(232, 200, 120, 0.6), transparent)",
        animation: "gold-shimmer 3s ease-in-out infinite",
      }}
    />
    {/* Multi-layer radial gradients for depth */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 60% 50% at 0% 0%, rgba(139, 92, 214, 0.12), transparent 60%)",
        mixBlendMode: "screen",
      }}
    />
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 40% 40% at 100% 100%, rgba(184, 146, 42, 0.08), transparent 70%)",
        mixBlendMode: "overlay",
      }}
    />
    {/* Subtle animated particles */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none opacity-30"
      style={{
        backgroundImage: `radial-gradient(circle at 20% 30%, ${GOLD_BRIGHT} 1px, transparent 1px),
                         radial-gradient(circle at 70% 60%, ${GOLD_BRIGHT} 1px, transparent 1px),
                         radial-gradient(circle at 40% 80%, ${GOLD_BRIGHT} 1px, transparent 1px)`,
        backgroundSize: "80px 80px, 100px 100px, 60px 60px",
        animation: "particle-float 20s ease-in-out infinite",
      }}
    />
  </>
);

const INTEGRATION_ROW_1 = [
  { id: "payfast", name: "PayFast", src: payfastLogo },
  { id: "peach", name: "Peach Payments", src: peachPaymentsLogo },
  { id: "paystack", name: "Paystack", src: paystackLogo },
  { id: "ozow", name: "Ozow", src: ozowLogo },
  { id: "yoco", name: "Yoco", src: yocoLogo },
  { id: "stitch", name: "Stitch Money", src: stitchMoneyLogo },
];

const INTEGRATION_ROW_2 = [
  { id: "stitch", name: "Stitch Money", src: stitchMoneyLogo },
  { id: "ozow", name: "Ozow", src: ozowLogo },
  { id: "paystack", name: "Paystack", src: paystackLogo },
  { id: "payfast", name: "PayFast", src: payfastLogo },
  { id: "yoco", name: "Yoco", src: yocoLogo },
  { id: "peach", name: "Peach Payments", src: peachPaymentsLogo },
];

export function IntegrationsSection() {
  return (
    <section
      id="integrations"
      className="bg-void px-6 flex items-center justify-center"
      style={{ paddingTop: 100, paddingBottom: 100 }}
      data-testid="integrations-section"
    >
      <style>
        {`
          .cl-int-inner {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 64px;
            max-width: 1080px;
            width: 100%;
            align-items: center;
          }

          .cl-int-card {
            background: var(--bg-surface);
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 24px;
            padding: 32px 0;
            overflow: hidden;
            position: relative;
          }

          .cl-int-card::before,
          .cl-int-card::after {
            content: "";
            position: absolute;
            top: 0;
            bottom: 0;
            width: 80px;
            z-index: 2;
            pointer-events: none;
          }

          .cl-int-card::before {
            left: 0;
            background: linear-gradient(to right, var(--bg-surface), transparent);
          }

          .cl-int-card::after {
            right: 0;
            background: linear-gradient(to left, var(--bg-surface), transparent);
          }

          .cl-int-h2 { font-size: 44px; }
          .cl-int-body { font-size: 17px; }

          .cl-int-rows {
            display: grid;
            gap: 14px;
          }

          .cl-int-row {
            overflow: hidden;
            width: 100%;
          }

          .cl-int-track {
            display: flex;
            gap: 12px;
            width: max-content;
            will-change: transform;
            transform: translate3d(0,0,0);
          }

          .cl-int-track.left {
            animation: cl-int-scroll-left 28s linear infinite;
          }

          .cl-int-track.right {
            animation: cl-int-scroll-right 34s linear infinite;
          }

          @keyframes cl-int-scroll-left {
            from { transform: translate3d(0,0,0); }
            to { transform: translate3d(-50%,0,0); }
          }

          @keyframes cl-int-scroll-right {
            from { transform: translate3d(-50%,0,0); }
            to { transform: translate3d(0,0,0); }
          }

          .cl-int-row:hover .cl-int-track {
            animation-play-state: paused;
          }

          .cl-int-pill {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #FFFFFF;
            border: 1px solid rgba(9,8,10,0.10);
            border-radius: 14px;
            padding: 12px 20px;
            height: 52px;
            flex-shrink: 0;
            transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
            cursor: default;
          }

          .cl-int-pill:hover {
            border-color: rgba(184,146,42,0.35);
            background: #FFFFFF;
            box-shadow: 0 10px 24px rgba(0,0,0,0.18);
          }

          .cl-int-pill img {
            height: 22px;
            width: auto;
            max-width: 90px;
            object-fit: contain;
            filter: none;
            opacity: 1;
            transition: opacity 0.2s ease;
            display: block;
          }

          .cl-int-pill:hover img {
            opacity: 1;
          }

          .cl-int-feature-pill {
            background: rgba(184,146,42,0.07);
            border: 1px solid rgba(184,146,42,0.18);
            border-radius: 999px;
            padding: 7px 14px;
            font-family: "Fira Code", monospace;
            font-size: 11px;
            letter-spacing: 0.07em;
            color: var(--gold-mid);
            white-space: nowrap;
          }

          @media (max-width: 768px) {
            .cl-int-inner {
              grid-template-columns: 1fr;
              gap: 40px;
            }
            .cl-int-card {
              padding: 24px 0;
            }
            .cl-int-h2 { font-size: 32px; }
            .cl-int-body { font-size: 15px; }
          }
        `}
      </style>

      <div className="cl-int-inner">
        <div>
          <div
            className="font-mono uppercase"
            style={{
              fontWeight: 600,
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--gold-primary)",
              marginBottom: 16,
            }}
          >
            NATIVE INTEGRATIONS
          </div>

          <h2
            className="font-display cl-int-h2"
            style={{
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.08,
              color: "var(--white-pure)",
              marginBottom: 20,
            }}
          >
            Built for How South Africa Actually Gets Paid.
          </h2>

          <p
            className="font-body cl-int-body"
            style={{
              fontWeight: 400,
              lineHeight: 1.75,
              color: "var(--text-secondary)",
              maxWidth: 400,
              marginBottom: 32,
            }}
          >
            Plug directly into South Africa&apos;s top payment rails. Zero manual reconciliation.
            Every failed transaction flows into Crown Ledger&apos;s recovery engine the moment it
            happens.
          </p>

          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <span className="cl-int-feature-pill">REST API</span>
            <span className="cl-int-feature-pill">WEBHOOKS</span>
            <span className="cl-int-feature-pill">REAL-TIME SYNC</span>
          </div>
        </div>

        <div className="cl-int-card" aria-label="Payment processor logos">
          <div className="cl-int-rows" aria-hidden="true">
            <div className="cl-int-row">
              <div className="cl-int-track left">
                {[...INTEGRATION_ROW_1, ...INTEGRATION_ROW_1].map((logo, idx) => (
                  <div
                    key={`${logo.id}-${idx}`}
                    className="cl-int-pill"
                    style={idx === 4 ? { backgroundColor: "#00a9e0" } : undefined}
                  >
                    <img src={logo.src} alt="" />
                  </div>
                ))}
              </div>
            </div>

            <div className="cl-int-row">
              <div className="cl-int-track right">
                {[...INTEGRATION_ROW_2, ...INTEGRATION_ROW_2].map((logo, idx) => (
                  <div
                    key={`${logo.id}-${idx}`}
                    className="cl-int-pill"
                    style={idx === 4 ? { backgroundColor: "#00a9e0" } : undefined}
                  >
                    <img src={logo.src} alt="" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ======================== CARD 2 — WHITE GLOVE ======================== */
function CardWhiteGlove() {
  const [isHovered, setIsHovered] = React.useState(false);
  
  return (
    <motion.div
      variants={fadeUp}
      style={{
        ...cardStyle,
        padding: 40,
        borderImage: isHovered 
          ? "linear-gradient(135deg, rgba(232, 200, 120, 0.6), rgba(139, 92, 214, 0.4), rgba(232, 200, 120, 0.6)) 1"
          : "linear-gradient(135deg, rgba(201, 168, 76, 0.2), rgba(139, 92, 214, 0.1)) 1",
        boxShadow: isHovered
          ? "0 20px 40px rgba(184, 146, 42, 0.15), 0 10px 20px rgba(139, 92, 214, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "0 10px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
      }}
      className="col-span-12 md:col-span-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ 
        scale: 1.02, 
        rotateX: 2, 
        rotateY: -2,
        y: -8,
        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
      }}
      data-testid="features-card-whiteglove"
    >
      <CardOverlays />
      <div className="relative">
        <Pill>White Glove</Pill>
        <h3
          className="font-cormorant mt-6"
          style={{ 
            fontSize: 32, 
            fontWeight: 600, 
            color: CREAM, 
            lineHeight: 1.15,
            textShadow: isHovered ? "0 2px 10px rgba(184, 146, 42, 0.3)" : "none",
            transition: "all 0.3s ease"
          }}
        >
          Done with you, not for you to figure out
        </h3>
        <p
          className="font-syne mt-5"
          style={{ 
            fontSize: 14, 
            color: CREAM_LO, 
            lineHeight: 1.75,
            letterSpacing: "0.01em"
          }}
        >
          No learning curve, no confusion. Your dedicated Crown Ledger setup
          specialist joins you live over video call, walks you through
          everything, and stays available free of charge around the clock.
        </p>

        {/* Premium video call mockup */}
        <div
          className="mt-8 overflow-hidden"
          style={{
            ...innerCardStyle,
            borderRadius: 16,
            border: "1px solid rgba(139, 92, 214, 0.3)",
            background: "linear-gradient(135deg, rgba(26, 22, 35, 0.8), rgba(14, 12, 19, 0.9))",
            backdropFilter: "blur(16px)",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4)",
          }}
        >
          {/* Enhanced header */}
          <div
            className="flex items-center gap-3 px-4 py-3"
            style={{ 
              background: "linear-gradient(135deg, rgba(184, 146, 42, 0.08), rgba(139, 92, 214, 0.06))", 
              borderBottom: "1px solid rgba(139, 92, 214, 0.2)" 
            }}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ 
                background: "#22C55E", 
                animation: "online-pulse 1.8s ease-in-out infinite",
                boxShadow: "0 0 10px rgba(34, 197, 94, 0.5)"
              }}
            />
            <span className="font-syne flex-1" style={{ 
              fontSize: 12, 
              color: GOLD_BRIGHT, 
              fontWeight: 600,
              letterSpacing: "0.02em"
            }}>
              Setup Specialist · Crown Ledger
            </span>
            <span
              className="font-syne flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #E8C878, #B8922A)",
                fontSize: 10,
                fontWeight: 700,
                color: "#07050E",
                boxShadow: "0 2px 8px rgba(184, 146, 42, 0.3)",
              }}
            >
              CL
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1.25fr_1fr] gap-4 p-4">
            <div
              className="relative h-[180px] md:h-[160px] rounded-xl overflow-hidden group"
              style={{
                border: "1px solid rgba(139, 92, 214, 0.2)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
              }}
            >
              <img 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                src={zoomMeetingDemo} 
                alt="Zoom meeting demo" 
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.4))",
                }}
              />
              {/* Floating overlay effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: "radial-gradient(circle at 50% 50%, rgba(184, 146, 42, 0.1), transparent 70%)",
                }}
              />
            </div>

            <div
              className="flex flex-col justify-center rounded-xl px-4 py-4 md:h-[160px]"
              style={{
                background: "linear-gradient(135deg, rgba(184, 146, 42, 0.06), rgba(139, 92, 214, 0.04))",
                border: "1px solid rgba(139, 92, 214, 0.15)",
                backdropFilter: "blur(8px)",
              }}
            >
              <div className="font-syne" style={{ 
                fontSize: 13.5, 
                fontWeight: 600, 
                color: GOLD_BRIGHT, 
                lineHeight: 1.35,
                letterSpacing: "0.01em"
              }}>
                Let me walk you through the setup
                <span className="inline-flex items-center gap-1 ml-2 align-middle">
                  {[0, 0.2, 0.4].map((d, i) => (
                    <span
                      key={i}
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{
                        background: GOLD_BRIGHT,
                        animation: `typing-dot 1.2s ease-in-out ${d}s infinite`,
                        boxShadow: "0 0 6px rgba(184, 146, 42, 0.5)",
                      }}
                    />
                  ))}
                </span>
              </div>
              <div className="font-syne mt-3" style={{ 
                fontSize: 11.5, 
                color: CREAM_LO, 
                lineHeight: 1.6,
                letterSpacing: "0.005em"
              }}>
                Live video onboarding with your dedicated setup specialist.
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ======================== CARD 3 — PERFORMANCE PRICING ======================== */
function CardPricing() {
  const [isHovered, setIsHovered] = React.useState(false);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];
  const heights = [28, 38, 46, 52, 60, 72, 95];
  
  return (
    <motion.div
      variants={fadeUp}
      style={{
        ...cardStyle,
        padding: 40,
        borderImage: isHovered 
          ? "linear-gradient(135deg, rgba(34, 197, 94, 0.6), rgba(184, 146, 42, 0.4), rgba(34, 197, 94, 0.6)) 1"
          : "linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(184, 146, 42, 0.1)) 1",
        boxShadow: isHovered
          ? "0 20px 40px rgba(34, 197, 94, 0.15), 0 10px 20px rgba(184, 146, 42, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "0 10px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
      }}
      className="col-span-12 md:col-span-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ 
        scale: 1.02, 
        rotateX: -2, 
        rotateY: 2,
        y: -8,
        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
      }}
      data-testid="features-card-pricing"
    >
      <CardOverlays />
      <div className="relative">
        <Pill>Aligned Incentives</Pill>
        <h3
          className="font-cormorant mt-6"
          style={{ 
            fontSize: 32, 
            fontWeight: 600, 
            color: CREAM, 
            lineHeight: 1.15,
            textShadow: isHovered ? "0 2px 10px rgba(34, 197, 94, 0.3)" : "none",
            transition: "all 0.3s ease"
          }}
        >
          Pure performance-based pricing
        </h3>
        <p
          className="font-syne mt-5"
          style={{ 
            fontSize: 14, 
            color: CREAM_LO, 
            lineHeight: 1.75,
            letterSpacing: "0.01em"
          }}
        >
          Zero setup costs, zero monthly fees, zero risk. We only earn when
          your recovered revenue hits your account.
        </p>

        {/* Premium chart */}
        <div 
          className="mt-8 p-6" 
          style={{
            ...innerCardStyle,
            background: "linear-gradient(135deg, rgba(26, 22, 35, 0.8), rgba(14, 12, 19, 0.9))",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            borderRadius: 16,
          }}
        >
          <div className="flex items-end justify-between gap-3 h-[160px]">
            {months.map((m, i) => {
              const isHighlight = i === months.length - 1;
              return (
                <div key={m} className="flex flex-col items-center justify-end h-full" style={{ flex: 1 }}>
                  <div
                    className="group relative"
                    style={{
                      width: "100%",
                      height: `${heights[i]}%`,
                      borderRadius: 6,
                      background: isHighlight
                        ? "linear-gradient(135deg, #22C55E, #16A34A, #15803D)"
                        : "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(184, 146, 42, 0.08))",
                      border: isHighlight
                        ? "1px solid rgba(34, 197, 94, 0.6)"
                        : "1px solid rgba(34, 197, 94, 0.25)",
                      boxShadow: isHighlight
                        ? "0 0 20px rgba(34, 197, 94, 0.4), 0 4px 12px rgba(34, 197, 94, 0.2)"
                        : "0 2px 8px rgba(0, 0, 0, 0.2)",
                      transformOrigin: "bottom",
                      animation: `bar-rise 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 0.08}s both`,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {/* Hover glow effect */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md"
                      style={{
                        background: "radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.2), transparent 70%)",
                      }}
                    />
                  </div>
                  <span
                    className="font-syne mt-3 transition-all duration-300"
                    style={{
                      fontSize: isHighlight ? 10 : 9,
                      color: isHighlight ? "#22C55E" : CREAM_LO,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontWeight: isHighlight ? 700 : 500,
                      textShadow: isHighlight ? "0 0 8px rgba(34, 197, 94, 0.4)" : "none",
                    }}
                  >
                    {m}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Premium recovery badge */}
          <div
            className="font-syne mt-6 inline-flex items-center gap-2 px-4 py-2.5 group cursor-default"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#22C55E",
              background: "linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(34, 197, 94, 0.08))",
              border: "1px solid rgba(34, 197, 94, 0.30)",
              borderRadius: 12,
              backdropFilter: "blur(8px)",
              boxShadow: "0 4px 12px rgba(34, 197, 94, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              transition: "all 0.3s ease",
            }}
          >
            <span 
              style={{ 
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "rgba(34, 197, 94, 0.2)",
                color: "#22C55E",
                fontWeight: 700,
              }}
            >
              ↑
            </span>
            <span style={{ letterSpacing: "0.02em" }}>
              R 84,220 recovered this month
            </span>
            {/* Animated glow on hover */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"
              style={{
                background: "radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.1), transparent 70%)",
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ======================== CARD 4 — SECURITY & COMPLIANCE ======================== */
function CardSecurity() {
  const [isHovered, setIsHovered] = React.useState(false);
  
  const privacyPolicyAnimationData = React.useMemo(() => {
    if (!privacyPolicyAnimation || typeof privacyPolicyAnimation !== "object") return privacyPolicyAnimation;
    if (!Array.isArray(privacyPolicyAnimation.assets)) return privacyPolicyAnimation;

    let changed = false;
    const assets = privacyPolicyAnimation.assets.map((asset) => {
      if (!asset || typeof asset !== "object") return asset;
      const p = typeof asset.p === "string" ? asset.p : "";
      const isEmbeddedImage = p.startsWith("data:image/");
      if (!isEmbeddedImage) return asset;
      if (asset.e === 1) return asset;
      changed = true;
      return { ...asset, e: 1 };
    });

    if (!changed) return privacyPolicyAnimation;
    return { ...privacyPolicyAnimation, assets };
  }, []);

  return (
    <motion.div
      variants={fadeUp}
      style={{
        ...cardStyle,
        padding: 40,
        borderImage: isHovered 
          ? "linear-gradient(135deg, rgba(139, 92, 214, 0.6), rgba(184, 146, 42, 0.4), rgba(139, 92, 214, 0.6)) 1"
          : "linear-gradient(135deg, rgba(139, 92, 214, 0.2), rgba(184, 146, 42, 0.1)) 1",
        boxShadow: isHovered
          ? "0 20px 40px rgba(139, 92, 214, 0.15), 0 10px 20px rgba(184, 146, 42, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "0 10px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
      }}
      className="col-span-12"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ 
        scale: 1.01, 
        rotateX: 1, 
        rotateY: -1,
        y: -6,
        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
      }}
      data-testid="features-card-security"
    >
      <CardOverlays />
      <div className="relative grid grid-cols-1 md:grid-cols-[45%_55%] gap-12 items-center">
        {/* Premium visual left */}
        <div
          style={{
            ...innerCardStyle,
            background: "linear-gradient(135deg, rgba(139, 92, 214, 0.15), rgba(184, 146, 42, 0.08), rgba(139, 92, 214, 0.05))",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(139, 92, 214, 0.3)",
            borderRadius: 20,
          }}
          className="relative h-[300px] flex items-center justify-center overflow-hidden group"
        >
          {/* Animated background glow */}
          <div 
            className="absolute inset-0 opacity-60"
            style={{
              background: "radial-gradient(circle at 50% 50%, rgba(139, 92, 214, 0.2), transparent 70%)",
              animation: "pulse-glow 4s ease-in-out infinite",
            }}
          />
          <div style={{ 
            animation: "shield-float 4s ease-in-out infinite", 
            position: "relative",
            filter: isHovered ? "drop-shadow(0 20px 32px rgba(139, 92, 214, 0.4))" : "drop-shadow(0 14px 24px rgba(0,0,0,0.45))",
            transition: "filter 0.3s ease"
          }}>
            <Lottie
              animationData={privacyPolicyAnimationData}
              loop
              autoplay
              style={{
                width: 180,
                height: 180,
                borderRadius: 16,
                opacity: 0.95,
              }}
            />
          </div>
          {/* Floating particles */}
          <div 
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 92, 214, 0.3) 2px, transparent 2px),
                               radial-gradient(circle at 75% 75%, rgba(184, 146, 42, 0.3) 2px, transparent 2px)`,
              backgroundSize: "60px 60px, 80px 80px",
              animation: "particle-drift 15s ease-in-out infinite",
            }}
          />
        </div>

        {/* Enhanced right text */}
        <div>
          <Pill>Security and Compliance</Pill>
          <h3
            className="font-cormorant mt-6"
            style={{ 
              fontSize: 32, 
              fontWeight: 600, 
              color: CREAM, 
              lineHeight: 1.15,
              textShadow: isHovered ? "0 2px 10px rgba(139, 92, 214, 0.3)" : "none",
              transition: "all 0.3s ease"
            }}
          >
            Elevated compliance, zero compromise
          </h3>
          <p
            className="font-syne mt-5"
            style={{ 
              fontSize: 14, 
              color: CREAM_LO, 
              lineHeight: 1.75,
              letterSpacing: "0.01em"
            }}
          >
            Every payment processor, every API integration, and every data
            touchpoint has been independently vetted by certified compliance
            specialists. Your transition into Crown Ledger meets full South
            African regulatory requirements and POPIA data protection
            standards.
          </p>

          <ul className="mt-7 flex flex-col gap-4">
            {[
              "POPIA compliant data handling",
              "256-bit AES encryption in transit", 
              "Expert-vetted processor onboarding",
            ].map((row, index) => (
              <li key={row} className="flex items-center gap-4 group">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 24, 
                    height: 24,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.08))",
                    border: "1px solid rgba(34, 197, 94, 0.35)",
                    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.12)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Check size={13} color="#22C55E" strokeWidth={3} />
                </span>
                <span
                  className="font-syne transition-all duration-300"
                  style={{ 
                    fontSize: 13, 
                    color: isHovered ? "rgba(240,235,225,0.65)" : "rgba(240,235,225,0.48)", 
                    letterSpacing: "0.01em",
                    transform: isHovered ? "translateX(2px)" : "translateX(0)"
                  }}
                >
                  {row}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

/* ======================== SECTION ======================== */
export default function Features() {
  return (
    <section
      id="features"
      className="relative overflow-hidden"
      style={{ background: "#07050E", paddingTop: 140, paddingBottom: 140 }}
      data-testid="features-section"
    >
      {/* Radial purple bloom — top 18% */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: "-10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "80%",
          height: "55%",
          background:
            "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(107, 33, 168, 0.18), transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      {/* Grain noise overlay — 3% */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.03,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
        }}
      />

      <style>{`
        section[data-testid="features-section"] .cl-section-label {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          margin-bottom: 16px;
        }

        section[data-testid="features-section"] .cl-section-rule {
          width: 60px;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.55) 50%,
            transparent 100%
          );
        }

        section[data-testid="features-section"] .cl-section-label-text {
          font-family: "Syne", system-ui, -apple-system, sans-serif;
          font-weight: 600;
          font-size: 11px;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.14em;
        }

        /* Premium animations */
        @keyframes gold-shimmer {
          0%, 100% { opacity: 0.6; transform: translateX(-10px); }
          50% { opacity: 1; transform: translateX(10px); }
        }

        @keyframes particle-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(120deg); }
          66% { transform: translateY(5px) rotate(240deg); }
        }

        @keyframes particle-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(10px, -5px) scale(1.1); }
          50% { transform: translate(-5px, 10px) scale(0.9); }
          75% { transform: translate(-10px, -5px) scale(1.05); }
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.05); }
        }

        @keyframes shield-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }

        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.2); }
        }

        @keyframes bar-rise {
          0% { transform: scaleY(0); opacity: 0; }
          100% { transform: scaleY(1); opacity: 1; }
        }

        @keyframes online-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
          50% { opacity: 0.8; box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
        }
      `}</style>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        {/* Eyebrow + Headline */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="text-center max-w-[640px] mx-auto"
        >
          <div className="cl-section-label">
            <span className="cl-section-rule" aria-hidden="true" />
            <span className="cl-section-label-text">What you get?</span>
            <span className="cl-section-rule" aria-hidden="true" />
          </div>

          <motion.h2
            variants={fadeUp}
            className="font-cormorant mt-5"
            style={{
              fontSize: "clamp(36px, 5.2vw, 56px)",
              fontWeight: 700,
              color: CREAM,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
            }}
          >
            Everything you need to{" "}
            <span style={{ fontStyle: "italic", color: GOLD, fontWeight: 600 }}>
              never lose money again
            </span>
          </motion.h2>
        </motion.div>

        {/* Premium Bento Grid */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-12 mx-auto mt-16"
          style={{ gap: 24, maxWidth: 1100 }}
        >
          <CardWhiteGlove />
          <CardPricing />
          <CardSecurity />
        </motion.div>

        {/* Premium CTA row */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="flex items-center justify-center gap-6 mt-20"
        >
          <span
            aria-hidden="true"
            className="hidden md:block"
            style={{ 
              width: 140, 
              height: 1, 
              background: "linear-gradient(90deg, transparent, rgba(232, 200, 120, 0.3), transparent)" 
            }}
          />
          <motion.button
            onClick={() =>
              document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })
            }
            className="font-syne group relative overflow-hidden"
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#07050E",
              background: "linear-gradient(135deg, #E8C878, #C9A84C, #B8922A)",
              borderRadius: 16,
              padding: "16px 42px",
              border: 0,
              cursor: "pointer",
              letterSpacing: "0.02em",
              boxShadow: "0 16px 40px rgba(184, 146, 42, 0.25), 0 8px 20px rgba(0, 0, 0, 0.3)",
              transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
            whileHover={{ 
              scale: 1.05, 
              y: -4,
              boxShadow: "0 20px 50px rgba(184, 146, 42, 0.35), 0 12px 30px rgba(0, 0, 0, 0.4)"
            }}
            whileTap={{ scale: 0.98 }}
            data-testid="features-cta"
          >
            {/* Button shine effect */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: "linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.3) 50%, transparent 60%)",
                animation: "button-shine 2s ease-in-out infinite",
              }}
            />
            <span className="relative z-10">
              Start recovering revenue today
            </span>
          </motion.button>
          <span
            aria-hidden="true"
            className="hidden md:block"
            style={{ 
              width: 140, 
              height: 1, 
              background: "linear-gradient(90deg, transparent, rgba(232, 200, 120, 0.3), transparent)" 
            }}
          />
        </motion.div>

        {/* Add button shine animation */}
        <style>{`
          @keyframes button-shine {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }
        `}</style>
      </div>
    </section>
  );
}
